import React, { useCallback, useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardTitle, Table, Spinner } from 'reactstrap';
import { ref, get, query, orderByKey, equalTo } from 'firebase/database';
import moment from 'moment';
import { db } from '../../../../firebase';
import ParagraphComparison from '../../QCReports/ParagraphComparison';
import { errorToast } from '../../../common/toast';

const UserReport = () => {
    const [userData, setUserData] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
    const [packageData, setPackageData] = useState(null);
    const [completedForms, setCompletedForms] = useState(0);
    const [correctForms, setCorrectForms] = useState(0);
    const [incorrectForms, setIncorrectForms] = useState(0);
    const [loading, setLoading] = useState(true);
    const [submittedForms, setSubmittedForms] = useState([]);

    // Fetch package details for the user
    const fetchPackageData = async () => {
        try {
            if (userData?.plan?.value) {
                const packageRef = ref(db, `packages/${userData.plan.value}`);
                const packageSnapshot = await get(packageRef);
                if (packageSnapshot.exists()) {
                    setPackageData(packageSnapshot.val());
                }
            }
        } catch (error) {
            errorToast("Error fetching package data:", error);
        }
    };

    // Fetch user form and QC report data
    const fetchFormsData = async () => {
        try {
            const formsRef = ref(db, `userForms`);
            const reportsRef = ref(db, `qcReports`);

            // Fetch all user forms and reports
            const [formsSnapshot, reportsSnapshot] = await Promise.all([get(formsRef), get(reportsRef)]);

            const formsData = formsSnapshot.val() || {};
            const reportsData = reportsSnapshot.val() || {};

            // Filter forms and reports based on userId
            const userForms = Object.values(formsData).filter(form => form.userId === userData?.uid);
            const userReport = Object.values(reportsData).find(report => report.userId === userData?.uid);

            const paraKeys = Object.keys(userForms[0] || {}).filter(key => key.startsWith('para'));

            setCompletedForms(paraKeys.length);
            setCorrectForms(userReport?.rightForms || 0);
            setIncorrectForms(userReport?.wrongForms || 0);
        } catch (error) {
            errorToast("Error fetching forms data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSubmittedForms = useCallback(async () => {
        try {
            const formsRef = ref(db, `userForms`);
            const formsSnapshot = await get(formsRef);
            const formsData = formsSnapshot.val() || {};
            const userForms = Object.values(formsData).filter(form => form.userId === userData?.uid);
            if (!userForms.length) return setSubmittedForms([]);

            const formsArray = Object.entries(userForms[0])
                .filter(([key]) => key.startsWith('para'))
                .map(([key, value]) => ({
                    ...value,
                    name: key,
                    id: parseInt(key.slice(4), 10),
                    mistakes: 0,
                }))
                .sort((a, b) => a.id - b.id);

            setSubmittedForms(formsArray);
        } catch (error) {
            errorToast("Error fetching submitted forms:", error);
        } finally {
            setLoading(false);
        }
    }, [userData]);

    const handleMistakeCount = (formId, mistakeCount) => {
        setSubmittedForms((prevForms) =>
            prevForms.map((form) =>
                form.id === formId ? { ...form, mistakes: mistakeCount } : form
            )
        );
    };

    useEffect(() => {
        fetchPackageData();
        fetchFormsData();
        fetchSubmittedForms();
    }, [fetchSubmittedForms]);

    const remainingForms = (packageData?.noOfForms || 0) - completedForms;

    const endDateWithTime = moment(userData?.endDate).endOf('day');
    const isBeforeEndDate = moment().isBefore(endDateWithTime);

    return (
        <Container className="mt-4">
            {!(userData?.endDate && isBeforeEndDate) ? (
                <>{loading ? (
                    <div className="text-center">
                        <Spinner color="primary" />
                        <p>Loading User Report...</p>
                    </div>
                ) : (
                    <>
                        <Row>
                            <Col md="12">
                                <Card>
                                    <CardBody>
                                        <CardTitle tag="h4">User Report</CardTitle>
                                        <Table bordered>
                                            <tbody>
                                                <tr>
                                                    <th>Name</th>
                                                    <td>{userData?.name || 'N/A'}</td>
                                                </tr>
                                                <tr>
                                                    <th>Email</th>
                                                    <td>{userData?.email || 'N/A'}</td>
                                                </tr>
                                                <tr>
                                                    <th>Package</th>
                                                    <td>{packageData?.noOfForms || 'N/A'} Forms</td>
                                                </tr>
                                                <tr>
                                                    <th>Plan Duration</th>
                                                    <td>{packageData?.planDuration || 'N/A'} Days</td>
                                                </tr>
                                                <tr>
                                                    <th>Start Date</th>
                                                    <td>{userData?.startDate || 'N/A'}</td>
                                                </tr>
                                                <tr>
                                                    <th>End Date</th>
                                                    <td>{userData?.endDate || 'N/A'}</td>
                                                </tr>
                                                <tr>
                                                    <th>Forms Completed</th>
                                                    <td>{completedForms}</td>
                                                </tr>
                                                <tr>
                                                    <th>Correct Forms</th>
                                                    <td>{correctForms}</td>
                                                </tr>
                                                <tr>
                                                    <th>Incorrect Forms</th>
                                                    <td>{incorrectForms}</td>
                                                </tr>
                                                <tr>
                                                    <th>Remaining Forms</th>
                                                    <td>{remainingForms >= 0 ? remainingForms : 'N/A'}</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                        <Row>
                            {submittedForms.map((form) => (
                                <Col md="12" key={form.id} className="mb-4">
                                    <Card>
                                        <CardBody>
                                            <CardTitle tag="h5">Form {form.id}</CardTitle>
                                            <Row>
                                                <Col xs="6">
                                                    <strong>Original Paragraph:</strong>
                                                    <div className="no-copy-card">
                                                        <p>{form.original || "No data available"}</p>
                                                    </div>
                                                </Col>
                                                <Col xs="6">
                                                    <ParagraphComparison
                                                        original={form.original || ""}
                                                        userInput={form.userInput || ""}
                                                        onMistakeCount={(mistakeCount) =>
                                                            handleMistakeCount(form.id, mistakeCount)
                                                        }
                                                    />
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </>)}
                </>
            ) : (
                <div className="text-center">
                    <p>Your report is pending because your work duration is not completed yet.</p>
                </div>
            )}
        </Container>
    );
};

export default UserReport;
